// Локальный fallback-стейт для Foundation-shell.
// Контракт зеркалит будущие Zustand-сторы из `@/stores/sidebar`, `@/stores/side-panel`,
// `@/stores/mobile-drawer`. Когда сторы-агент закончит работу — этот файл можно
// удалить, заменив импорты на `@/stores/*`.
import { useCallback, useEffect, useRef, useState } from "react";

const SIDEBAR_KEY = "studyhub:sidebar:collapsed";
const isBrowser = typeof window !== "undefined";

// Статические разделы `/courses/*`, которые НЕ являются воркспейсом курса:
// список курсов (`/courses`), глобальный учебный календарь (`/courses/calendar`),
// каталог курсов (`/courses/catalog`, легаси-URL `/courses/templates`) и бриф
// создания курса (`/courses/new` — полноэкранный мастер, но не воркспейс: курса
// на этом шаге ещё нет, и скоуп репетитора резолвил бы «курс с id `new`»).
// Календарь курса — это режим центральной сцены, отдельного URL у него нет (§4.1).
//
// Список ОБЯЗАН содержать каждый статический сегмент под `/courses/`: предикат
// работает «всё, кроме перечисленного = воркспейс», поэтому забытая страница
// молча получает свёрнутый рельс и скрытый топбар. Границу держит тест, который
// перебирает статические роуты из `routeTree.gen.ts`.
const NON_WORKSPACE_COURSE_SEGMENTS = new Set(["calendar", "catalog", "templates", "new"]);

/**
 * Роут «воркспейс курса» — полноэкранный обучающий центр:
 * `/courses/$courseId` и `/courses/$courseId_/topics/$topicId` (§4.1).
 *
 * ЕДИНСТВЕННЫЙ предикат воркспейса на всё приложение (декрет E8). Копировать
 * тело в другие файлы запрещено — расхождение предикатов подсветки/скрытия
 * это известный класс багов. Потребители:
 *   • `main-layout.tsx` — скрытие топбара и BottomNav, режим полной высоты,
 *     бюджет ширины для панелей курса;
 *   • `topbar.tsx` — скрытие «дневной нормы» (её рисует шапка курса);
 *   • `audio-mini-player.tsx` — гейт видимости (иначе в курсе два плеера и
 *     двойной вычет `--audio-player-height`);
 *   • `hooks/use-mini-player.ts` — не закрывать плеер по `ended`;
 *   • `tutor-panel-course-scope.ts` — курсовой скоуп глобальной панели
 *     репетитора (состав курса, тема, шаг).
 *
 * Панель репетитора в воркспейсе БОЛЬШЕ НЕ ПОДАВЛЯЕТСЯ: с этапа 1 плана единого
 * репетитора она работает в курсе со скоупом на курс, а `routes/__root.tsx` и
 * `topbar.tsx` этот предикат для неё не спрашивают.
 *
 * Намеренно НЕ расширяет `isFullBleedChatRoute` — это второй, независимый предикат.
 */
export function isWorkspaceRoute(pathname: string): boolean {
	if (!pathname.startsWith("/courses/")) return false;
	const segment = pathname.slice("/courses/".length).split("/")[0] ?? "";
	if (segment.length === 0) return false;
	return !NON_WORKSPACE_COURSE_SEGMENTS.has(segment);
}

/**
 * Меняет ли экран КАРКАС приложения — прячет шапку и нижнюю навигацию.
 *
 * Нужен для выбора вида перехода. Горизонтальный выезд предполагает, что два
 * экрана — соседи одного каркаса: они едут бок о бок, и это читается верно.
 * Но воркспейс курса каркас заменяет целиком, поэтому в кадре оказывались две
 * шапки и два нижних бара сразу (снято скринкастом 2026-08-27). Нативные
 * приложения такие экраны не сдвигают вбок, а накрывают снизу — накрывающий
 * закрывает предыдущий целиком, и показывать рядом нечего.
 */
export function changesAppShell(pathname: string): boolean {
	if (isWorkspaceRoute(pathname)) return true;
	// Полноэкранный чат репетитора прячет ту же пару баров.
	if (!pathname.startsWith("/tutor/")) return false;
	if (pathname === "/tutor/scenarios" || pathname === "/tutor/skills") return false;
	if (pathname.startsWith("/voice-tutor")) return false;
	return true;
}

function readSidebarPersisted(): boolean {
	if (!isBrowser) return false;
	try {
		return window.localStorage.getItem(SIDEBAR_KEY) === "1";
	} catch {
		return false;
	}
}

export interface SidebarState {
	isCollapsed: boolean;
	toggle: () => void;
	setCollapsed: (next: boolean) => void;
}

/**
 * Состояние рельса приложения.
 *
 * @param forceCollapsed Принудительно свёрнутый рельс (режим воркспейса курса).
 * Пользовательский выбор при этом НЕ трогается: ни state, ни localStorage — при
 * выходе из курса рельс возвращается в то состояние, в котором был. Наивный
 * `setCollapsed(true)` записал бы `"1"` в localStorage (эффект ниже пишет на
 * каждое изменение) и рельс остался бы свёрнутым «навсегда».
 */
export function useSidebarLayoutState(forceCollapsed = false): SidebarState {
	const [userCollapsed, setUserCollapsed] = useState<boolean>(readSidebarPersisted);

	useEffect(() => {
		if (!isBrowser) return;
		try {
			window.localStorage.setItem(SIDEBAR_KEY, userCollapsed ? "1" : "0");
		} catch {
			// ignore — приватный режим / квота
		}
	}, [userCollapsed]);

	// Ref-зеркало: колбэки обязаны остаться стабильными — они сидят в deps
	// эффекта авто-сворачивания в MainLayout.
	const forcedRef = useRef(forceCollapsed);
	forcedRef.current = forceCollapsed;

	const setCollapsed = useCallback((next: boolean): void => {
		if (forcedRef.current) return;
		setUserCollapsed(next);
	}, []);
	const toggle = useCallback((): void => {
		if (forcedRef.current) return;
		setUserCollapsed((p) => !p);
	}, []);

	return { isCollapsed: forceCollapsed || userCollapsed, toggle, setCollapsed };
}

export type ActiveSidePanel = "tutor" | "notifications" | null;

export interface SidePanelState {
	activePanel: ActiveSidePanel;
	open: (panel: Exclude<ActiveSidePanel, null>) => void;
	close: () => void;
	toggle: (panel: Exclude<ActiveSidePanel, null>) => void;
}

export function useSidePanelLayoutState(): SidePanelState {
	const [activePanel, setActivePanel] = useState<ActiveSidePanel>(null);
	const open = useCallback(
		(panel: Exclude<ActiveSidePanel, null>): void => setActivePanel(panel),
		[],
	);
	const close = useCallback((): void => setActivePanel(null), []);
	const toggle = useCallback(
		(panel: Exclude<ActiveSidePanel, null>): void =>
			setActivePanel((prev) => (prev === panel ? null : panel)),
		[],
	);
	return { activePanel, open, close, toggle };
}

export interface MobileDrawerState {
	isOpen: boolean;
	open: () => void;
	close: () => void;
	setOpen: (next: boolean) => void;
}

export function useMobileDrawerState(): MobileDrawerState {
	const [isOpen, setOpen] = useState(false);
	const open = useCallback((): void => setOpen(true), []);
	const close = useCallback((): void => setOpen(false), []);
	return { isOpen, open, close, setOpen };
}

// Theme stub — toggle light↔dark, default = system. Полноценный стор появится в `@/stores/theme`.
type ThemePref = "light" | "dark" | "system";

function readSystemTheme(): "light" | "dark" {
	if (!isBrowser) return "light";
	return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(resolved: "light" | "dark"): void {
	if (!isBrowser) return;
	const root = document.documentElement;
	if (resolved === "dark") {
		root.classList.add("dark");
		root.setAttribute("data-theme", "dark");
	} else {
		root.classList.remove("dark");
		root.setAttribute("data-theme", "light");
	}
}

export interface ThemeState {
	resolved: "light" | "dark";
	toggle: () => void;
}

export function useThemeLayoutState(): ThemeState {
	const [pref, setPref] = useState<ThemePref>("system");
	const [resolved, setResolved] = useState<"light" | "dark">(readSystemTheme);

	useEffect(() => {
		if (!isBrowser) return;
		const next = pref === "system" ? readSystemTheme() : pref;
		setResolved(next);
		applyTheme(next);
	}, [pref]);

	useEffect(() => {
		if (!isBrowser) return;
		const mql = window.matchMedia("(prefers-color-scheme: dark)");
		const handler = (): void => {
			if (pref === "system") {
				const next = readSystemTheme();
				setResolved(next);
				applyTheme(next);
			}
		};
		mql.addEventListener("change", handler);
		return () => mql.removeEventListener("change", handler);
	}, [pref]);

	const toggle = useCallback(
		(): void => setPref((prev) => (prev === "dark" ? "light" : "dark")),
		[],
	);
	return { resolved, toggle };
}
